/**
 * CoffeeClubCartUpsell - Shows Bula Coffee Pass as final upsell in cart
 * Now supports both Standard ($95) and Extended ($120) passes
 * Only shown to users who don't have an active pass
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, X, Star, Clock, CheckCircle, Plus, Sun, Sunrise } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

// Mini Coffee Pass Logo
const CoffeeClubMiniLogo = ({ size = 40 }) => (
  <div 
    className="rounded-full bg-primary/10 flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    <Coffee className="text-primary" style={{ width: size * 0.5, height: size * 0.5 }} />
  </div>
);

export default function CoffeeClubCartUpsell({ 
  userEmail, 
  userPhone, 
  onAddToCart,
  onClose,
  isVisible = true 
}) {
  const [hasActivePass, setHasActivePass] = useState(null);
  const [passInfo, setPassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('extended'); // Default to extended for better value

  // Check if user already has a pass
  useEffect(() => {
    const checkPass = async () => {
      if (!userEmail && !userPhone) {
        setHasActivePass(false);
        setLoading(false);
        return;
      }

      try {
        const params = new URLSearchParams();
        if (userEmail) params.append('email', userEmail);
        if (userPhone) params.append('phone', userPhone);

        const res = await api.get(`/coffee-club/status?${params}`);
        setHasActivePass(res.data.has_active_pass);
        setPassInfo(res.data);
      } catch (err) {
        console.error('Failed to check coffee club status:', err);
        setHasActivePass(false);
      } finally {
        setLoading(false);
      }
    };

    checkPass();
  }, [userEmail, userPhone]);

  const handleAddStandardPass = () => {
    onAddToCart({
      id: 'bula-coffee-pass',
      name: 'Bula Coffee Pass - Standard',
      price: 95.00,
      quantity: 1,
      type: 'coffee_club_pass',
      plan_type: 'standard',
      description: '1 free 8oz hot coffee daily (7-10 AM or 1-3:30 PM) for 12 months',
      image: null,
      isSpecialItem: true
    });
    
    toast.success('Standard Coffee Pass added!', {
      description: 'Redemption: 7-10 AM or 1-3:30 PM'
    });
    
    setDismissed(true);
  };

  const handleAddExtendedPass = () => {
    onAddToCart({
      id: 'bula-coffee-pass-extended',
      name: 'Bula Coffee Pass - Extended',
      price: 95.00,
      quantity: 1,
      type: 'coffee_club_pass',
      plan_type: 'extended',
      description: '1 free 8oz coffee daily 7-10 AM + 1-3:30 PM for 12 months',
      image: null,
      isSpecialItem: true
    });
    
    toast.success('Extended Coffee Pass added!', {
      description: 'Morning + Afternoon redemption windows'
    });
    
    setDismissed(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onClose) onClose();
  };

  // Don't show if:
  // - Still loading
  // - User already has active pass
  // - User dismissed it
  // - Not visible
  if (loading || hasActivePass || dismissed || !isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, height: 0 }}
        animate={{ opacity: 1, y: 0, height: 'auto' }}
        exit={{ opacity: 0, y: -20, height: 0 }}
        className="relative"
        data-testid="coffee-club-cart-upsell"
      >
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-amber-500/10 border border-primary/20 rounded-2xl p-4 mb-4">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-background/50 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          <div className="flex items-start gap-4">
            {/* Logo */}
            <CoffeeClubMiniLogo size={48} />

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-heading text-base text-foreground">Bula Coffee Pass</h4>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-medium rounded-full">
                  Best Value
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">
                Get <strong className="text-foreground">1 free 8oz coffee every day</strong> for a whole year!
              </p>

              {/* Plan Selection */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {/* Standard Plan */}
                <button
                  onClick={() => setSelectedPlan('standard')}
                  data-testid="select-plan-standard"
                  className={`p-2 rounded-lg border text-left transition-all ${
                    selectedPlan === 'standard' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-1 mb-1">
                    <Sunrise className="w-3 h-3 text-amber-500" />
                    <span className="text-xs font-medium">Standard</span>
                  </div>
                  <div className="text-lg font-bold text-primary">$95</div>
                  <div className="text-[10px] text-muted-foreground">7-10 AM or 1-3:30 PM</div>
                </button>

                {/* Extended Plan */}
                <button
                  onClick={() => setSelectedPlan('extended')}
                  data-testid="select-plan-extended"
                  className={`p-2 rounded-lg border text-left transition-all relative ${
                    selectedPlan === 'extended' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span className="absolute -top-2 -right-1 px-1.5 py-0.5 bg-green-500 text-white text-[8px] font-bold rounded-full">
                    +PM
                  </span>
                  <div className="flex items-center gap-1 mb-1">
                    <Sun className="w-3 h-3 text-amber-500" />
                    <span className="text-xs font-medium">Extended</span>
                  </div>
                  <div className="text-lg font-bold text-primary">$95</div>
                  <div className="text-[10px] text-muted-foreground">7-10 AM + 1-3:30 PM</div>
                </button>
              </div>

              {/* CTA */}
              <Button
                size="sm"
                className="w-full rounded-full"
                onClick={selectedPlan === 'extended' ? handleAddExtendedPass : handleAddStandardPass}
                data-testid="add-coffee-club-to-cart"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add {selectedPlan === 'extended' ? 'Extended' : 'Standard'} Pass
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Simplified version for inline use
export function CoffeeClubInlinePromo({ onLearnMore }) {
  return (
    <div 
      className="flex items-center gap-3 p-3 bg-primary/5 rounded-xl cursor-pointer hover:bg-primary/10 transition-colors"
      onClick={onLearnMore}
      data-testid="coffee-club-inline-promo"
    >
      <CoffeeClubMiniLogo size={40} />
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">Bula Coffee Pass</p>
        <p className="text-xs text-muted-foreground">Free daily coffee from $95/year</p>
      </div>
      <span className="text-primary text-sm font-medium">Learn More →</span>
    </div>
  );
}
